import { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import StatusBadge from "../components/StatusBadge";
import { api, formatErrorDetail } from "../lib/api";
import { Check, X, Eye, Search } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminApplications() {
    const [applications, setApplications] = useState([]);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/applications" + (filter !== "all" ? `?status_filter=${filter}` : ""));
            setApplications(res.data);
        } finally { setLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filter]);

    const updateStatus = async (id, status) => {
        try {
            const res = await api.patch(`/admin/applications/${id}`, { status });
            setApplications((prev) => prev.map((a) => a.id === id ? res.data : a));
            if (selected?.id === id) setSelected(res.data);
            toast.success(`Application ${status}`);
        } catch (err) {
            toast.error(formatErrorDetail(err.response?.data?.detail));
        }
    };

    const filtered = applications.filter((a) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return a.company_name.toLowerCase().includes(q) ||
            a.applicant_name.toLowerCase().includes(q) ||
            a.application_no.toLowerCase().includes(q);
    });

    return (
        <AdminLayout>
            <div className="mb-8 flex items-end justify-between" data-testid="applications-page">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Membership Pipeline</div>
                    <h1 className="font-display text-3xl font-bold text-[#0F172A] tracking-tight">Applications</h1>
                </div>
                <div className="text-sm text-[#64748B] tabular">{filtered.length} {filter !== "all" ? filter : "total"}</div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-[#E2E8F0] mb-px">
                <div className="flex items-center justify-between p-4 gap-4 flex-wrap">
                    <div className="flex gap-1" data-testid="status-filter">
                        {["all", "pending", "approved", "rejected"].map((s) => (
                            <button
                                key={s} onClick={() => setFilter(s)}
                                data-testid={`filter-${s}`}
                                className={`px-4 py-2 text-xs uppercase tracking-[0.1em] font-medium transition-colors ${filter === s ? "bg-[#0F172A] text-white" : "bg-white text-[#64748B] hover:text-[#0F172A] border border-[#E2E8F0]"}`}
                            >{s}</button>
                        ))}
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                        <input
                            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                            data-testid="search-applications"
                            placeholder="Search company, applicant, ref…"
                            className="pl-10 pr-4 py-2 border border-[#E2E8F0] text-sm w-80 focus:outline-none focus:border-[#0F172A]"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white border border-[#E2E8F0]">
                {loading ? (
                    <div className="p-10 text-center text-sm text-[#64748B]">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center text-sm text-[#64748B]" data-testid="applications-empty">No applications found</div>
                ) : (
                    <table className="w-full text-sm" data-testid="applications-table">
                        <thead className="text-xs uppercase tracking-wide text-[#64748B] bg-[#F8F9FA]">
                            <tr>
                                <th className="text-left px-6 py-3 font-medium">Application #</th>
                                <th className="text-left px-6 py-3 font-medium">Company</th>
                                <th className="text-left px-6 py-3 font-medium">Applicant</th>
                                <th className="text-left px-6 py-3 font-medium">Category</th>
                                <th className="text-left px-6 py-3 font-medium">Status</th>
                                <th className="text-left px-6 py-3 font-medium">Submitted</th>
                                <th className="text-right px-6 py-3 font-medium">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E2E8F0]">
                            {filtered.map((a) => (
                                <tr key={a.id} data-testid={`app-row-${a.id}`} className="hover:bg-[#F8F9FA]">
                                    <td className="px-6 py-4 font-mono text-xs">{a.application_no}</td>
                                    <td className="px-6 py-4 font-medium">{a.company_name}</td>
                                    <td className="px-6 py-4 text-[#64748B]">{a.applicant_name}</td>
                                    <td className="px-6 py-4 text-[#64748B]">{a.category}</td>
                                    <td className="px-6 py-4"><StatusBadge status={a.status} /></td>
                                    <td className="px-6 py-4 text-[#64748B] tabular">{formatDate(a.submitted_at)}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-1 justify-end">
                                            <button onClick={() => setSelected(a)} data-testid={`view-app-${a.id}`} className="p-2 hover:bg-blue-50 text-blue-600" title="View"><Eye className="w-4 h-4" /></button>
                                            {a.status === "pending" && (
                                                <>
                                                    <button onClick={() => updateStatus(a.id, "approved")} data-testid={`approve-app-${a.id}`} className="p-2 hover:bg-green-50 text-green-600" title="Approve"><Check className="w-4 h-4" /></button>
                                                    <button onClick={() => updateStatus(a.id, "rejected")} data-testid={`reject-app-${a.id}`} className="p-2 hover:bg-red-50 text-red-600" title="Reject"><X className="w-4 h-4" /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Detail Drawer */}
            {selected && (
                <div data-testid="app-detail-modal" className="fixed inset-0 z-50 bg-[#0F172A]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
                    <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <div className="text-xs font-mono text-[#D4AF37] mb-1">{selected.application_no}</div>
                                <h3 className="font-display text-2xl font-bold text-[#0F172A]">{selected.company_name}</h3>
                                <div className="mt-2"><StatusBadge status={selected.status} /></div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-[#64748B] hover:text-[#0F172A]" data-testid="close-detail-modal"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="grid grid-cols-2 gap-6 text-sm">
                            <DetailRow label="Applicant" value={selected.applicant_name} />
                            <DetailRow label="Email" value={selected.email} />
                            <DetailRow label="Phone" value={selected.phone} />
                            <DetailRow label="Category" value={selected.category} />
                            <DetailRow label="GST" value={selected.gst_number || "—"} />
                            <DetailRow label="Employees" value={selected.employee_count || "—"} />
                            <DetailRow label="Turnover" value={selected.annual_turnover || "—"} />
                            <DetailRow label="Member ID" value={selected.member_id || "—"} />
                            <DetailRow label="Address" value={`${selected.address}, ${selected.city}, ${selected.state}`} full />
                            {selected.about_company && <DetailRow label="About" value={selected.about_company} full />}
                        </div>

                        {selected.status === "pending" && (
                            <div className="flex gap-2 mt-8 pt-6 border-t border-[#E2E8F0]">
                                <button onClick={() => updateStatus(selected.id, "approved")} data-testid="detail-approve-btn" className="flex-1 px-4 py-3 bg-green-600 text-white text-sm font-semibold hover:bg-green-700">Approve</button>
                                <button onClick={() => updateStatus(selected.id, "rejected")} data-testid="detail-reject-btn" className="flex-1 px-4 py-3 bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Reject</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

function DetailRow({ label, value, full }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <div className="text-xs uppercase tracking-[0.1em] text-[#64748B] mb-1">{label}</div>
            <div className="text-[#0F172A]">{value}</div>
        </div>
    );
}
